import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IntelligenceTypes } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useState } from 'react';
import { useProgramContext } from '../../../layouts/ProgramLayout';
import { programRef } from '../../../lib/access/types';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { IntelligenceFailureNotice } from '../_shared/components/IntelligenceFailureNotice';
import { IntelligenceReextractButton } from '../_shared/components/IntelligenceReextractButton';
import { EmptyState } from '../_shared/components/PageStates';
import { ProposalReview } from '../_shared/components/ProposalReview';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useProgram } from '../Programs/hooks';
import { useCPARSScores, useDeleteCPARSScore, useUpsertCPARSScore } from './hooks';
import { narrativeFieldBindings, narrativeFormField } from './narrativeFields';
import { OverallDerivation } from './overallDerivation';
import { ScoreAttachmentLink } from './ScoreAttachmentLink';
import { ScoreDocumentUpload } from './ScoreDocumentUpload';
// The per-area narratives this tab binds a form field to, in form order. Feeds
// CPARS_FIELD_BINDINGS below, the textarea `baseFields` entries (placed under
// each area's own score), and cparsDetailSections' drawer inline edit — one
// source of truth for all three (SAD-154/SAD-161: QASP and CPARS must not
// drift on shared behaviour).
const CPARS_NARRATIVE_AREAS = [
    {
        destination: IntelligenceTypes.DestinationFieldName.QUALITY_NARRATIVE,
        formField: 'qualityNarrative',
        label: 'Quality narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.SCHEDULE_NARRATIVE,
        formField: 'scheduleNarrative',
        label: 'Schedule narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.COST_CONTROL_NARRATIVE,
        formField: 'costControlNarrative',
        label: 'Cost control narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.MANAGEMENT_NARRATIVE,
        formField: 'managementNarrative',
        label: 'Management narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.SMALL_BUSINESS_NARRATIVE,
        formField: 'smallBusinessNarrative',
        label: 'Small business narrative'
    },
    {
        destination: IntelligenceTypes.DestinationFieldName.REGULATORY_COMPLIANCE_NARRATIVE,
        formField: 'regulatoryComplianceNarrative',
        label: 'Regulatory compliance narrative'
    }
];
// Maps the CPARS extractor's canonical destination fields onto this form's
// react-hook-form field names. Date fields need the YYYY-MM-DD string shape
// the existing date inputs use; score/entry fields need numbers.
const CPARS_FIELD_BINDINGS = {
    // No ENTRY_NUMBER binding: the entry number is system-assigned (SAD-171),
    // so an extracted value must never overwrite it. An unbound proposal is
    // still shown (read-only, no Apply) by ProposalReview — it just can't
    // land on the draft.
    [IntelligenceTypes.DestinationFieldName.PERIOD_START]: {
        formField: 'evalPeriodStart',
        label: 'Eval period start',
        parse: (v)=>String(v)
    },
    [IntelligenceTypes.DestinationFieldName.PERIOD_END]: {
        formField: 'evalPeriodEnd',
        label: 'Eval period end',
        parse: (v)=>String(v)
    },
    [IntelligenceTypes.DestinationFieldName.OVERALL_SCORE]: {
        formField: 'overallScore',
        label: 'Overall score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.QUALITY_SCORE]: {
        formField: 'qualityScore',
        label: 'Quality score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.SCHEDULE_SCORE]: {
        formField: 'scheduleScore',
        label: 'Schedule score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.COST_CONTROL_SCORE]: {
        formField: 'costControlScore',
        label: 'Cost control score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.MANAGEMENT_SCORE]: {
        formField: 'managementScore',
        label: 'Management score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.SMALL_BUSINESS_SCORE]: {
        formField: 'smallBusinessScore',
        label: 'Small business score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.REGULATORY_COMPLIANCE_SCORE]: {
        formField: 'regulatoryComplianceScore',
        label: 'Regulatory compliance score',
        parse: (v)=>Number(v)
    },
    [IntelligenceTypes.DestinationFieldName.OVERALL_COMMENT]: {
        formField: 'overallComment',
        label: 'Overall comment',
        parse: (v)=>String(v)
    },
    [IntelligenceTypes.DestinationFieldName.CONCURRENCE_STATUS]: {
        formField: 'concurrenceStatus',
        label: 'Concurrence status',
        parse: (v)=>String(v)
    },
    ...narrativeFieldBindings(CPARS_NARRATIVE_AREAS)
};
const concurrenceOptions = [
    {
        value: 'Pending',
        label: 'Pending'
    },
    {
        value: 'Concurred',
        label: 'Concurred'
    },
    {
        value: 'Non-concurred',
        label: 'Non-concurred'
    }
];
// Concurrence → semantic tone. Pending is unresolved (warning); concurred is
// agreement (success); non-concurred is a formal dispute (danger).
const CONCURRENCE_TONE = {
    Pending: 'warning',
    Concurred: 'success',
    'Non-concurred': 'danger'
};
// Eval-period columns are historical windows, not deadlines: right-aligned like
// dates, but no urgency coloring.
// Entry # is deliberately not shown here (SAD-171, Matt's direction): the
// number is still server-assigned, monotonic and non-reusing for ordering,
// identity and audit — it's just not a user-facing concept. Do not restore
// this column.
const columns = [
    {
        key: 'evalPeriodStart',
        header: 'Start',
        align: 'right',
        render: (row)=>row.evalPeriodStart ? formatLocalYmd(row.evalPeriodStart) ?? '—' : '—'
    },
    {
        key: 'evalPeriodEnd',
        header: 'End',
        align: 'right',
        render: (row)=>row.evalPeriodEnd ? formatLocalYmd(row.evalPeriodEnd) ?? '—' : '—'
    },
    {
        key: 'overallScore',
        header: 'Overall',
        align: 'right',
        render: (row)=>row.overallScore != null ? row.overallScore.toFixed(2) : '—',
        maskAs: 'cpars_score'
    },
    {
        key: 'concurrenceStatus',
        header: 'Concurrence',
        render: (row)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                variant: CONCURRENCE_TONE[row.concurrenceStatus] ?? 'neutral',
                children: row.concurrenceStatus || '—'
            })
    }
];
// Copy for a score the source document explicitly rated N/A. The field itself
// stays blank - `optional double` has no "N/A" to store, and inventing one (a
// 0, a sentinel) is the fabrication PR #995's zero-coercion fix exists to
// prevent. What was missing is that a blank-because-N/A and a
// blank-because-nobody-filled-it-in looked identical, so applying an N/A
// rating appeared to do nothing at all (SAD-154, matt 2026-08-27).
const NOT_APPLICABLE_HINT = 'Not applicable per the source document - left blank.';
// The six per-area ratings that roll up into Overall. Order matches the form.
const CPARS_CATEGORY_SCORE_FIELDS = [
    'qualityScore',
    'scheduleScore',
    'costControlScore',
    'managementScore',
    'smallBusinessScore',
    'regulatoryComplianceScore'
];
const baseFields = [
    {
        kind: 'date',
        name: 'evalPeriodStart',
        label: 'Eval period start',
        required: true
    },
    {
        kind: 'date',
        name: 'evalPeriodEnd',
        label: 'Eval period end',
        required: true
    },
    {
        kind: 'number',
        name: 'overallScore',
        label: 'Overall score (0–5)',
        min: 0,
        max: 5,
        step: 0.01
    },
    {
        kind: 'number',
        name: 'qualityScore',
        label: 'Quality',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(CPARS_NARRATIVE_AREAS[0]),
    {
        kind: 'number',
        name: 'scheduleScore',
        label: 'Schedule',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(CPARS_NARRATIVE_AREAS[1]),
    {
        kind: 'number',
        name: 'costControlScore',
        label: 'Cost control',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(CPARS_NARRATIVE_AREAS[2]),
    {
        kind: 'number',
        name: 'managementScore',
        label: 'Management',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(CPARS_NARRATIVE_AREAS[3]),
    {
        kind: 'number',
        name: 'smallBusinessScore',
        label: 'Small business',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(CPARS_NARRATIVE_AREAS[4]),
    {
        kind: 'number',
        name: 'regulatoryComplianceScore',
        label: 'Regulatory compliance',
        min: 0,
        max: 5,
        step: 0.01
    },
    narrativeFormField(CPARS_NARRATIVE_AREAS[5]),
    {
        kind: 'textarea',
        name: 'overallComment',
        label: 'Overall comment',
        rows: 2
    },
    {
        kind: 'select',
        name: 'concurrenceStatus',
        label: 'Concurrence status',
        options: concurrenceOptions
    }
];
// naFields carries the form-field names an applied proposal marked explicitly
// N/A, so those score inputs can say so instead of reading as simply unfilled.
const cparsFields = (naFields)=>baseFields.map((f)=>naFields.has(f.name) ? {
            ...f,
            helpText: NOT_APPLICABLE_HINT
        } : f);
// Form defaults for a given record (null = create). Shared by the create modal
// and the drawer's inline edit so both surfaces start from the same shape.
const cparsDefaults = (c)=>({
        id: c?.id ?? '',
        evalPeriodStart: c?.evalPeriodStart ? formatLocalYmd(c.evalPeriodStart) ?? '' : '',
        evalPeriodEnd: c?.evalPeriodEnd ? formatLocalYmd(c.evalPeriodEnd) ?? '' : '',
        // No `?? 0` fallback: a missing score must render blank, not zero.
        overallScore: c?.overallScore,
        qualityScore: c?.qualityScore,
        scheduleScore: c?.scheduleScore,
        costControlScore: c?.costControlScore,
        managementScore: c?.managementScore,
        smallBusinessScore: c?.smallBusinessScore,
        regulatoryComplianceScore: c?.regulatoryComplianceScore,
        overallComment: c?.overallComment ?? '',
        concurrenceStatus: c?.concurrenceStatus ?? 'Pending',
        // Seeded from the record (not just left blank) so an inline edit that never
        // touches a narrative still re-sends it — the upsert below is a full
        // replace, and this default is the only thing standing between an
        // unrelated score edit and silently wiping every narrative.
        qualityNarrative: c?.qualityNarrative ?? '',
        scheduleNarrative: c?.scheduleNarrative ?? '',
        costControlNarrative: c?.costControlNarrative ?? '',
        managementNarrative: c?.managementNarrative ?? '',
        smallBusinessNarrative: c?.smallBusinessNarrative ?? '',
        regulatoryComplianceNarrative: c?.regulatoryComplianceNarrative ?? ''
    });
// Read sections plus, per row, the inline-edit control. Rows that the create
// form covers carry an `editField` (bound to the drawer's form), including the
// per-area narratives now that the create form exposes them; the rest — get-
// well plan, concurrence comment, attachment — stay read-only even in edit
// mode (they're preserved through the upsert from the source row, not
// re-keyed here). Score rows keep their `masked` flag for the READ display:
// only a writer (canEdit) enters edit, and writers see full values, so
// surfacing the number control to them is not a leak.
function cparsDetailSections(c) {
    const score = (n)=>n != null ? n.toFixed(2) : '—';
    const ymd = (t)=>t ? formatLocalYmd(t) ?? '—' : '—';
    const scoreField = (name, label)=>({
            kind: 'number',
            name,
            label,
            min: 0,
            max: 5,
            step: 0.01
        });
    return [
        {
            heading: 'Evaluation period',
            rows: [
                {
                    label: 'Start',
                    value: ymd(c.evalPeriodStart),
                    editField: {
                        kind: 'date',
                        name: 'evalPeriodStart',
                        label: 'Eval period start',
                        required: true
                    }
                },
                {
                    label: 'End',
                    value: ymd(c.evalPeriodEnd),
                    editField: {
                        kind: 'date',
                        name: 'evalPeriodEnd',
                        label: 'Eval period end',
                        required: true
                    }
                }
            ]
        },
        {
            heading: 'Scores',
            rows: [
                {
                    label: 'Overall',
                    value: score(c.overallScore),
                    masked: true,
                    editField: scoreField('overallScore', 'Overall score (0–5)')
                },
                {
                    label: 'Quality',
                    value: score(c.qualityScore),
                    masked: true,
                    editField: scoreField('qualityScore', 'Quality')
                },
                {
                    label: 'Schedule',
                    value: score(c.scheduleScore),
                    masked: true,
                    editField: scoreField('scheduleScore', 'Schedule')
                },
                {
                    label: 'Cost control',
                    value: score(c.costControlScore),
                    masked: true,
                    editField: scoreField('costControlScore', 'Cost control')
                },
                {
                    label: 'Management',
                    value: score(c.managementScore),
                    masked: true,
                    editField: scoreField('managementScore', 'Management')
                },
                {
                    label: 'Small business',
                    value: score(c.smallBusinessScore),
                    masked: true,
                    editField: scoreField('smallBusinessScore', 'Small business')
                },
                {
                    label: 'Regulatory compliance',
                    value: score(c.regulatoryComplianceScore),
                    masked: true,
                    editField: scoreField('regulatoryComplianceScore', 'Regulatory compliance')
                }
            ]
        },
        {
            heading: 'Narratives',
            rows: [
                {
                    label: 'Overall comment',
                    value: c.overallComment || '—',
                    masked: true,
                    editField: {
                        kind: 'textarea',
                        name: 'overallComment',
                        label: 'Overall comment',
                        rows: 2
                    }
                },
                {
                    label: 'Quality',
                    value: c.qualityNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(CPARS_NARRATIVE_AREAS[0], 2)
                },
                {
                    label: 'Schedule',
                    value: c.scheduleNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(CPARS_NARRATIVE_AREAS[1], 2)
                },
                {
                    label: 'Cost control',
                    value: c.costControlNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(CPARS_NARRATIVE_AREAS[2], 2)
                },
                {
                    label: 'Management',
                    value: c.managementNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(CPARS_NARRATIVE_AREAS[3], 2)
                },
                {
                    label: 'Small business',
                    value: c.smallBusinessNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(CPARS_NARRATIVE_AREAS[4], 2)
                },
                {
                    label: 'Regulatory compliance',
                    value: c.regulatoryComplianceNarrative || '—',
                    masked: true,
                    editField: narrativeFormField(CPARS_NARRATIVE_AREAS[5], 2)
                },
                // Not exposed by the create form; preserved through the upsert.
                {
                    label: 'Get-well plan',
                    value: c.getWellPlan || '—',
                    masked: true
                }
            ]
        },
        {
            heading: 'Concurrence',
            rows: [
                {
                    label: 'Status',
                    value: c.concurrenceStatus || '—',
                    editField: {
                        kind: 'select',
                        name: 'concurrenceStatus',
                        label: 'Concurrence status',
                        options: concurrenceOptions
                    }
                },
                // Read-only: not part of the create form; preserved through the upsert.
                {
                    label: 'Comment',
                    value: c.concurrenceComment || '—',
                    masked: true
                }
            ]
        },
        // Masked: the source PDF holds the very scores we redact, so a restricted
        // viewer must not be able to download it. Read-only in edit mode — the
        // attachment id rides through the upsert from parent state.
        ...c.attachmentId ? [
            {
                heading: 'Attachment',
                rows: [
                    {
                        label: 'Document',
                        value: /*#__PURE__*/ _jsx(ScoreAttachmentLink, {
                            attachmentId: c.attachmentId
                        }),
                        masked: true,
                        maskFieldName: 'attachment_id'
                    }
                ]
            }
        ] : []
    ];
}
export function CPARSScoresTab({ programId }) {
    const { canEdit } = useProgramContext();
    const { data, isLoading } = useCPARSScores(programId);
    const upsert = useUpsertCPARSScore();
    const remove = useDeleteCPARSScore();
    // Delete is an exclusive allow-list (program manager / quality, per Matt's
    // review comment) computed server-side as the same "close" decision the
    // delete RPC enforces — never re-derive it client-side. Unlike
    // SubcontractorReviewsTab (which merely disables Delete for a caller
    // without canManageQuality), an unauthorized persona here must not even
    // see the affordance, so it's hidden entirely rather than disabled.
    const canDeleteQualityScore = useProgram(programId).data?.canDeleteQualityScore ?? false;
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    // Store only the selected id and DERIVE the record from `items` — so an open
    // drawer always reflects the latest list (e.g. after an inline-edit Save)
    // without an effect that re-syncs state. Syncing via effect infinite-loops
    // when the list returns fresh-but-equal references each render.
    const [detailId, setDetailId] = useState(null);
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // Holds the uploaded document's artifact id for the open surface. Seeded from
    // the edited row so the full-replace upsert (on artifact_id) doesn't drop it.
    const [attachmentId, setAttachmentId] = useState('');
    const [attachedName, setAttachedName] = useState('');
    const [uploading, setUploading] = useState(false);
    // Regenerated on each create-drawer open so it stays stable for that one
    // open (ProposalReview passes it through, unchanged, to MarkRunAppliedToDraft).
    const [draftId, setDraftId] = useState('');
    // Score fields an applied proposal marked explicitly N/A in the source
    // document, so the form can say so rather than leaving them silently blank.
    const [naFields, setNaFields] = useState(()=>new Set());
    // The run this draft's own Re-extract button just started, so the review
    // surface pins to it instead of the awaiting/failed-run queries' most
    // recent hit for this target - which, for a create dialog, has no entityId
    // yet and is shared across every open "New CPARS Score" dialog (SAD-321,
    // SAD-154). '' (not undefined) is the sentinel for "no run started yet for
    // this draft" (the first-run "Extract" verb below): IntelligenceFailureNotice
    // and ProposalReview both treat ANY defined pinnedRunId - including '' - as
    // "pinned, never fall back to the shared target's most recent run", so
    // leaving this undefined between opens would let a fresh dialog fall back
    // to, and immediately show, a FAILED/awaiting-review run some OTHER open
    // "New CPARS Score" dialog left behind for the same shared target. Once a
    // run actually starts, '' still means "started but no id back yet" - both
    // components already treat it identically to any other non-matching pin, so
    // there is no separate state to add. Mirrors DeviationsTab's
    // pinnedDeviationRunId, which never used undefined for this reason either.
    // Reset alongside draftId whenever a fresh create drawer opens.
    const [pinnedRunId, setPinnedRunId] = useState('');
    const openNew = ()=>{
        setAttachmentId('');
        setAttachedName('');
        setUploading(false);
        setNaFields(new Set());
        setDraftId(crypto.randomUUID());
        setPinnedRunId('');
        setFormOpen(true);
    };
    // Shared by the create modal and drawer edit-in-place; the target id is passed
    // in (empty = create). Get-well plan and concurrence comment aren't exposed
    // by either surface, so they're carried through from the source row so the
    // full-replace upsert doesn't wipe them. The entry number isn't sent at all:
    // the server assigns it on create and preserves it on update (SAD-171), so
    // this form has nothing to contribute there.
    const submitCPARS = async (values, id)=>{
        const src = id ? items.find((c)=>c.id === id) ?? null : null;
        await upsert.mutateAsync({
            id,
            programId,
            attachmentId,
            // A blank score field is `undefined` (see EntityForm's number input),
            // which must reach pangaea as an unset field, not a coerced 0.
            overallScore: values.overallScore,
            evalPeriodStart: parseLocalDate(values.evalPeriodStart),
            evalPeriodEnd: parseLocalDate(values.evalPeriodEnd),
            overallComment: values.overallComment ?? '',
            qualityScore: values.qualityScore,
            scheduleScore: values.scheduleScore,
            costControlScore: values.costControlScore,
            managementScore: values.managementScore,
            smallBusinessScore: values.smallBusinessScore,
            regulatoryComplianceScore: values.regulatoryComplianceScore,
            concurrenceStatus: values.concurrenceStatus ?? 'Pending',
            // Now bound to real form fields (create form + drawer inline edit): an
            // untouched narrative round-trips from `values` (itself seeded from the
            // source row by cparsDefaults), never fabricated. `src` is the fallback
            // only for a value the form never had a chance to seed at all.
            qualityNarrative: values.qualityNarrative ?? src?.qualityNarrative ?? '',
            scheduleNarrative: values.scheduleNarrative ?? src?.scheduleNarrative ?? '',
            costControlNarrative: values.costControlNarrative ?? src?.costControlNarrative ?? '',
            managementNarrative: values.managementNarrative ?? src?.managementNarrative ?? '',
            smallBusinessNarrative: values.smallBusinessNarrative ?? src?.smallBusinessNarrative ?? '',
            regulatoryComplianceNarrative: values.regulatoryComplianceNarrative ?? src?.regulatoryComplianceNarrative ?? '',
            // Not exposed by either surface; preserve the source row's values so the
            // full-replace upsert doesn't wipe them.
            getWellPlan: src?.getWellPlan ?? '',
            concurrenceComment: src?.concurrenceComment ?? ''
        });
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync({
            programId,
            id: deleting.id
        });
        setDeleting(null);
    };
    // The drawer's inline-edit config, bound to the viewed record.
    const detailEdit = detail ? {
        defaultValues: cparsDefaults(detail),
        onSubmit: (v)=>submitCPARS(v, detail.id),
        isSubmitting: upsert.isPending || uploading,
        submitLabel: 'Save changes',
        // Seed the attachment id from the record so an untouched edit re-sends
        // it (the drawer offers no re-upload control).
        onEditStart: ()=>setAttachmentId(detail.attachmentId ?? '')
    } : undefined;
    const newButton = /*#__PURE__*/ _jsx(SpecraButton, {
        onClick: openNew,
        disabled: !canEdit,
        "data-testid": Testids.deliveryQuality.cparsCreate,
        children: "New CPARS Score"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("h3", {
                        className: "text-[15px] font-medium",
                        children: [
                            "CPARS Scores ",
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-normal text-muted-foreground",
                                children: [
                                    "· ",
                                    items.length
                                ]
                            })
                        ]
                    }),
                    newButton
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryQuality.cparsTable,
                rowTestid: Testids.deliveryQuality.cparsRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                hideRowActions: !canEdit && !canDeleteQualityScore,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                resource: programRef(programId),
                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                    title: "No CPARS evaluations on file",
                    description: "The Satisfaction KPI reads empty until the first CPARS entry is recorded. Enter the assessing official's ratings and attach the source document.",
                    action: !canEdit ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: openNew,
                        children: "New CPARS Score"
                    })
                }),
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryQuality.cparsEdit,
                                children: "Edit"
                            }),
                            canDeleteQualityScore && /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                className: "text-danger hover:bg-danger-muted hover:text-danger",
                                onClick: ()=>setDeleting(row),
                                "data-testid": Testids.deliveryQuality.cparsDelete,
                                children: "Delete"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "New CPARS Score",
                fields: cparsFields(naFields),
                defaultValues: cparsDefaults(null),
                onSubmit: async (v)=>{
                    await submitCPARS(v, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending || uploading,
                extraContent: (form)=>/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(OverallDerivation, {
                                form: form,
                                categoryFields: CPARS_CATEGORY_SCORE_FIELDS,
                                overallField: "overallScore"
                            }),
                            /*#__PURE__*/ _jsx(ScoreDocumentUpload, {
                                label: "Upload CPARS document",
                                onUploaded: (id, name)=>{
                                    setAttachmentId(id);
                                    setAttachedName(name);
                                },
                                attachedName: attachedName,
                                onUploadingChange: setUploading
                            }),
                            /*#__PURE__*/ _jsx(IntelligenceReextractButton, {
                                workflowKind: IntelligenceTypes.WorkflowKind.CPARS,
                                target: {
                                    programId,
                                    entityType: 'CPARSScore'
                                },
                                sourceArtifactId: attachmentId,
                                canEdit: canEdit,
                                label: pinnedRunId === '' ? 'Extract from document' : 'Re-extract from document',
                                description: pinnedRunId === '' ? 'Processes the attached source document, right here in this drawer, to propose field values.' : 'Re-runs field extraction against the attached source document.',
                                verb: pinnedRunId === '' ? 'Extract' : 'Re-extract',
                                // '' means "started, but no run id yet" - ProposalReview and
                                // IntelligenceFailureNotice both render no panel for it and
                                // never fall back to another run for this shared target.
                                onStarted: (run)=>setPinnedRunId(run?.id ?? '')
                            }),
                            /*#__PURE__*/ _jsx(IntelligenceFailureNotice, {
                                target: {
                                    programId,
                                    entityType: 'CPARSScore'
                                },
                                workflowKind: IntelligenceTypes.WorkflowKind.CPARS,
                                pinnedRunId: pinnedRunId
                            }),
                            /*#__PURE__*/ _jsx(ProposalReview, {
                                target: {
                                    programId,
                                    entityType: 'CPARSScore'
                                },
                                workflowKind: IntelligenceTypes.WorkflowKind.CPARS,
                                draftId: draftId,
                                pinnedRunId: pinnedRunId,
                                fieldBindings: CPARS_FIELD_BINDINGS,
                                onApplyToDraft: (values)=>{
                                    for (const { formField, value } of values){
                                        form.setValue(formField, value);
                                    }
                                    // Recomputed from the whole payload rather than merged, so a
                                    // second apply that no longer calls a field N/A clears its hint.
                                    setNaFields(new Set(values.filter((v)=>v.notApplicable).map((v)=>v.formField)));
                                }
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: detail ? `CPARS Score — ${detail.evalPeriodStart ? formatLocalYmd(detail.evalPeriodStart) ?? '—' : '—'} – ${detail.evalPeriodEnd ? formatLocalYmd(detail.evalPeriodEnd) ?? '—' : '—'}` : '',
                sections: detail ? cparsDetailSections(detail) : [],
                resource: programRef(programId),
                maskAs: "cpars_score",
                testid: Testids.deliveryQuality.scoreDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete CPARS Score",
                description: deleting ? `Delete the CPARS score for eval period ${deleting.evalPeriodStart ? formatLocalYmd(deleting.evalPeriodStart) ?? '—' : '—'} – ${deleting.evalPeriodEnd ? formatLocalYmd(deleting.evalPeriodEnd) ?? '—' : '—'}? This cannot be undone.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            })
        ]
    });
}
